import React from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import { Box, Tooltip } from '@mui/material';

import useRecentRelease from '../../hooks/useRecentRelease';

interface VersionLinkProps {
  version: string;
  onClick: () => void;
  buildTimeLabel?: string;
}

export default function VersionLink({ version, onClick, buildTimeLabel }: VersionLinkProps) {
  const { isRecent, releaseDateLabel } = useRecentRelease(version);
  const tooltipTitle = buildTimeLabel
    ? `${buildTimeLabel} · Clique para ver as novidades`
    : 'Clique para ver as novidades desta versão';

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip arrow title={tooltipTitle} placement="top">
        <Box
          component="button"
          type="button"
          onClick={onClick}
          aria-label={`Versão ${version}. Ver notas de atualização.`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            border: '1px solid',
            borderColor: 'rgba(63, 81, 181, 0.25)',
            borderRadius: '999px',
            bgcolor: 'rgba(63, 81, 181, 0.06)',
            color: 'primary.main',
            fontSize: 'inherit',
            fontWeight: 700,
            letterSpacing: '0.02em',
            fontFamily: 'inherit',
            lineHeight: 1.2,
            cursor: 'pointer',
            transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            verticalAlign: 'middle',
            flexShrink: 0,
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
            '&:hover': {
              bgcolor: 'primary.main',
              borderColor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: '0 4px 12px rgba(63, 81, 181, 0.25)'
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2
            }
          }}
        >
          <NewReleasesOutlinedIcon sx={{ fontSize: '1em' }} />
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.25 }}>
            <Box
              component="span"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                opacity: 0.85,
                textTransform: 'uppercase'
              }}
            >
              v
            </Box>
            {version}
          </Box>
        </Box>
      </Tooltip>

      {isRecent && (
        <Tooltip
          arrow
          title={releaseDateLabel ? `Nova versão · Lançada em ${releaseDateLabel}` : 'Nova versão'}
          placement="top"
        >
          <AutoAwesomeIcon
            aria-label="Nova versão"
            sx={{
              fontSize: '1rem',
              color: 'warning.main',
              flexShrink: 0
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

VersionLink.defaultProps = {
  buildTimeLabel: undefined
};
